/**
 * User detail (ADMIN-PR1-b) — GET /v1/admin/users/{id}, with:
 *   - status toggle (active ⇄ suspended)  → POST /v1/admin/users/{id}/status
 *   - system-role (trustee) assignment    → B6 grant/revoke system-roles
 *
 * This is the user-search-backed system-role assignment screen: reached from the
 * Users list, so the operator finds the user_id via search and grants/revokes
 * super_admin / industry_trustee here. All dual-gated via adminApi.
 */
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getUser,
  grantSystemRole,
  revokeSystemRole,
  setUserStatus,
  type AdminUserDetail,
  type SystemRole,
  type UserStatus,
} from "../auth/adminApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";

const ASSIGNABLE_ROLES: { role: SystemRole; label: string; help: string }[] = [
  { role: "industry_trustee", label: "industry_trustee", help: "Masters trustee-write (stock curation)" },
  { role: "super_admin", label: "super_admin", help: "Global admin (this console)" },
];

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function UserDetailPage(): ReactNode {
  const { id = "" } = useParams();
  const { data, loading, error, reload } = useAsync<AdminUserDetail>(() => getUser(id), [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  async function onToggleStatus(current: string) {
    const next: UserStatus = current === "active" ? "suspended" : "active";
    if (!window.confirm(`Set this user's status to "${next}"?`)) return;
    setMutationError(null);
    setBusy("status");
    try {
      await setUserStatus(id, next);
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onToggleRole(role: SystemRole, has: boolean) {
    const verb = has ? "Revoke" : "Grant";
    if (!window.confirm(`${verb} the "${role}" system role for this user?`)) return;
    setMutationError(null);
    setBusy(role);
    try {
      if (has) await revokeSystemRole(id, role);
      else await grantSystemRole(id, role);
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={errorMessage(error)} />;
  if (!data) return <ErrorBanner message="User not found." />;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/users" className="text-sm text-oxbo hover:underline">
          ← Users
        </Link>
      </div>
      <PageHeader title={data.email} />

      {mutationError && <ErrorBanner message={mutationError} />}

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Identity</h2>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <Field label="User ID" value={<span className="font-mono text-xs">{data.id}</span>} />
          <Field label="Full name" value={data.full_name ?? "—"} />
          <Field label="Email verified" value={fmtDate(data.email_verified_at)} />
          <Field label="Created" value={fmtDate(data.created_at)} />
          <Field
            label="Org roles"
            value={data.org_roles.length ? data.org_roles.join(", ") : "—"}
          />
          <Field
            label="System roles"
            value={data.system_roles.length ? data.system_roles.join(", ") : "—"}
          />
        </dl>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Account status</h2>
        <div className="flex items-center gap-4">
          <StatusBadge status={data.status} />
          <button
            type="button"
            disabled={busy === "status"}
            onClick={() => void onToggleStatus(data.status)}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {data.status === "active" ? "Suspend" : "Reactivate"}
          </button>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-slate-700">System role assignment</h2>
        <p className="mb-3 text-xs text-slate-500">
          Global identity grants. <code>super_admin</code> gates this console;{" "}
          <code>industry_trustee</code> enables masters stock curation.
        </p>
        <ul className="space-y-2">
          {ASSIGNABLE_ROLES.map(({ role, label, help }) => {
            const has = data.system_roles.includes(role);
            return (
              <li
                key={role}
                className="flex items-center justify-between rounded border border-slate-100 px-3 py-2"
              >
                <div>
                  <span className="text-sm font-medium text-slate-700">{label}</span>
                  <span className="ml-2 text-xs text-slate-400">{help}</span>
                  {has && (
                    <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">
                      granted
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={busy === role}
                  onClick={() => void onToggleRole(role, has)}
                  className={`rounded border px-3 py-1 text-xs disabled:opacity-50 ${
                    has
                      ? "border-slate-300 text-red-600 hover:bg-red-50"
                      : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  }`}
                >
                  {has ? "Revoke" : "Grant"}
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-slate-700">{value}</dd>
    </div>
  );
}
